import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './DepositReceiptReview.css';

const money = value => `RM ${Number(value || 0).toLocaleString('en-MY', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const statusLabels = {
  pending: 'AI check pending', processing: 'AI check pending',
  no_discrepancy: 'No discrepancy found', check_this: 'Check this receipt',
  could_not_read: 'Could not read', unavailable: 'AI check unavailable',
};
const readerLabel = engineVersion => {
  if (engineVersion === 'local-tesseract-v1-claude-fallback') return 'Claude unavailable · local OCR used';
  if (engineVersion?.startsWith('claude-')) return 'Claude AI used';
  if (engineVersion === 'local-tesseract-v1') return 'Local OCR used';
  return null;
};
const label = value => String(value || '').replaceAll('_', ' ').replace(/^./, first => first.toUpperCase());
const fieldValue = (field, value) => value == null || value === '' ? 'Could not read'
  : field === 'amount' ? money(value)
    : field === 'method' ? label(value) : String(value);

export default function DepositReceiptReview({ source, bookingId, token, receiptRequired }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const urls = useRef([]);
  useEffect(() => () => { urls.current.forEach(url => URL.revokeObjectURL(url)); urls.current = []; }, []);

  if (receiptRequired === false) return <span className="receipt-legacy">Receipt not required (older booking)</span>;

  const close = () => {
    setOpen(false); setData(null); setError('');
    urls.current.forEach(url => URL.revokeObjectURL(url)); urls.current = [];
  };
  const show = async () => {
    if (open) { close(); return; }
    setOpen(true); setLoading(true); setError('');
    try {
      const response = await axios.get(`/api/${source}-bookings/${bookingId}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (requestError) { setError(requestError.response?.data?.error || 'Could not load receipts'); }
    finally { setLoading(false); }
  };
  const openReceipt = async payment => {
    const popup = window.open('', '_blank');
    setError('');
    if (!popup) {
      setError('Your browser blocked the receipt window. Allow pop-ups for this dashboard and try again.');
      return;
    }
    popup.opener = null;
    try {
      const response = await axios.get(`/api/${source}-bookings/${bookingId}/payments/${payment.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      urls.current.push(url);
      popup.location.href = url;
    } catch (requestError) {
      popup?.close();
      setError(requestError.response?.data?.error || 'Could not open receipt');
    }
  };
  const remaining = data?.required_deposit_amount == null ? null
    : Math.max(0, Number(data.required_deposit_amount) - Number(data.deposit_amount || 0));

  return <div className="receipt-review">
    <button type="button" className="receipt-review-button" onClick={show}>
      {open ? 'Close receipts' : 'Review receipts'}
    </button>
    {open && <div className="receipt-review-panel" role="dialog" aria-label="Deposit receipts" aria-modal="true">
      <button type="button" className="receipt-review-button receipt-review-close" onClick={close}>Close receipts</button>
      {loading && <p>Loading receipts...</p>}
      {error && <p role="alert" className="cop-error-text">{error}</p>}
      {data && <>
        <p><strong>Required:</strong> {data.required_deposit_amount == null ? 'Not entered' : money(data.required_deposit_amount)}
          {' · '}<strong>Received:</strong> {money(data.deposit_amount)}
          {' · '}<strong>Remaining:</strong> {remaining == null ? '—' : money(remaining)}</p>
        {data.payments.length === 0 && <p>No transaction receipts attached yet.</p>}
        {data.payments.map((payment, index) => <article className="receipt-review-payment" key={payment.id}>
          <h4>Payment {index + 1} · {money(payment.amount)} · {payment.received_date}</h4>
          <p className={`receipt-screen-status ${payment.screening_status || 'pending'}`}>
            {statusLabels[payment.screening_status] || 'AI check pending'}
          </p>
          {readerLabel(payment.screening_engine_version) && <p className="receipt-screen-reader">
            {readerLabel(payment.screening_engine_version)}
          </p>}
          <button type="button" className="receipt-review-button" onClick={() => openReceipt(payment)}>
            Open {payment.receipt_filename}
          </button>
          <div className="receipt-review-comparison">
            <strong>Field</strong><strong>Entered</strong><strong>Read from receipt</strong>
            {['amount', 'date', 'method', 'reference'].map(field => <React.Fragment key={field}>
              <span>{label(field)}</span>
              <span>{fieldValue(field, field === 'date' ? payment.received_date : payment[field])}</span>
              <span>{fieldValue(field, payment.screening_extracted?.[field])}</span>
            </React.Fragment>)}
          </div>
          {(payment.screening_flags || []).length > 0 && <ul className="receipt-review-flags">
            {payment.screening_flags.map((flag, flagIndex) => <li key={`${flag.code}-${flagIndex}`}>{label(flag.code)}</li>)}
          </ul>}
        </article>)}
        <p className="receipt-review-manual">Always compare every payment with the bank or terminal statement before approval. OCR findings do not confirm that money arrived.</p>
      </>}
    </div>}
  </div>;
}
